import React, { useEffect, useState } from "react";
import { XCircle, Loader2, AlertTriangle, Trash2, Factory, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Bouton « Annuler la commande » avec gestion cascade (OFs + BL + lots).
 *
 * Logique métier :
 *  - Si aucun OF/BL lié → annulation simple (status=ANNULEE)
 *  - Si OFs PLANIFIE/EN_COURS → cascade simple (annule juste les OFs)
 *  - Si OF TERMINE → suppression des lots créés ET de leurs mouvements IN
 *  - BLOQUE si un lot a déjà été vendu/livré/jeté ou si BL signé / facture émise
 *
 * Le bouton ouvre un dialog 2 étapes :
 *  1. Aperçu de l'impact (GET /client-orders/{id}/cancel-impact)
 *  2. Confirmation et application (POST /client-orders/{id}/cancel-cascade)
 */
export default function CancelCascadeDialog({ co, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) {
      setImpact(null);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/client-orders/${co.id}/cancel-impact`);
        setImpact(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur");
        setOpen(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, co.id]);

  const apply = async () => {
    setApplying(true);
    try {
      await api.post(`/client-orders/${co.id}/cancel-cascade`);
      toast.success("Commande et entités liées annulées");
      setOpen(false);
      if (onCancelled) await onCancelled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'annulation cascade");
    } finally {
      setApplying(false);
    }
  };

  // Phase 101du.6 — Force cancel : ignore les blockers (lots utilisés).
  // Utilisé quand un BL a été effacé après signature/livraison → les mouvements
  // OUT/TRANSFER restent en base et bloquent la cascade classique.
  const forceApply = async () => {
    const confirmed = window.confirm(
      "ANNULATION FORCÉE — cette action est irréversible.\n\n"
      + "• La commande sera marquée ANNULEE\n"
      + "• Les OF liés seront ANNULES\n"
      + "• Les lots intacts seront SUPPRIMÉS\n"
      + `• Les ${totalBlockingLots} lot(s) déjà utilisés seront ARCHIVÉS (traçabilité HACCP préservée, qtés restantes intactes)\n\n`
      + "Continuer ?"
    );
    if (!confirmed) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/client-orders/${co.id}/force-cancel-cascade`);
      toast.success(data.cancellation_summary || "Commande force-annulée");
      setOpen(false);
      if (onCancelled) await onCancelled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du force-cancel");
    } finally {
      setApplying(false);
    }
  };

  const totalRemovableLots = (impact?.of_to_cancel_with_lots || []).reduce(
    (acc, of) => acc + (of.removable_lots?.length || 0), 0
  );
  const totalBlockingLots = (impact?.of_to_cancel_with_lots || []).reduce(
    (acc, of) => acc + (of.blocking_lots?.length || 0), 0
  );

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="sm"
        className="text-[#C05C47]"
        onClick={() => setOpen(true)}
        data-testid="co-cancel"
      >
        <XCircle className="w-4 h-4 mr-1.5" /> Annuler la commande
      </Button>

      <AlertDialogContent className="max-w-2xl" data-testid="co-cancel-cascade-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-[#C05C47]" />
            Annuler la commande {co?.reference} ?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left">
            L'annulation est effectuée en cascade sur les entités liées (OFs et BL).
            Si un OF était déjà terminé, les lots qu'il a créés en stock sont aussi supprimés.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-6 text-[#575A57]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Analyse de l'impact…
          </div>
        )}

        {impact && (
          <div className="space-y-3 text-sm">
            {impact.blocked && (
              <div className="border-2 border-[#C05C47] bg-[#FCEEEA] rounded p-3 flex gap-2" data-testid="cascade-blocked">
                <AlertTriangle className="w-5 h-5 text-[#C05C47] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-[#C05C47] mb-1">Annulation impossible</div>
                  <ul className="text-xs space-y-1 list-disc pl-4">
                    {impact.blockers.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                  <div className="text-xs text-[#5C2F22] mt-2 italic">
                    Régularisez d&apos;abord ces éléments (transfert, signature, perte) avant de pouvoir annuler.
                  </div>
                  <div className="text-xs text-[#7A3324] mt-2 border-t border-[#E8C1B4] pt-2">
                    <b>OU</b> utilisez le bouton rouge foncé <b>« Annuler quand même (force) »</b> ci-dessous — les lots utilisés seront archivés et la traçabilité HACCP conservée.
                  </div>
                </div>
              </div>
            )}

            {!impact.blocked && (
              <div className="space-y-2">
                {impact.of_to_cancel_simple.length > 0 && (
                  <div className="border border-[#E5E5E0] rounded p-2.5 bg-[#FAFAF7]">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-[#1A1C1A] mb-1">
                      <Factory className="w-3.5 h-3.5 text-[#8C6B2F]" />
                      {impact.of_to_cancel_simple.length} OF planifié(s) / en cours seront annulés
                    </div>
                    <div className="text-[11px] text-[#575A57] flex gap-1.5 flex-wrap">
                      {impact.of_to_cancel_simple.map((of) => (
                        <span key={of.id} className="font-mono">{of.reference}</span>
                      ))}
                    </div>
                  </div>
                )}

                {impact.of_to_cancel_with_lots.length > 0 && (
                  <div className="border border-[#E8D3A8] rounded p-2.5 bg-[#FBF4E9]/40">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-[#8C6B2F] mb-1">
                      <Trash2 className="w-3.5 h-3.5" />
                      {impact.of_to_cancel_with_lots.length} OF terminé(s) → suppression de {totalRemovableLots} lot(s) en stock
                    </div>
                    {impact.of_to_cancel_with_lots.map((of) => (
                      <div key={of.id} className="text-[11px] mt-1.5 border-l-2 border-[#E8D3A8] pl-2">
                        <span className="font-mono text-[#8C6B2F]">{of.reference}</span>
                        <span className="text-[#5C4A1F]"> · {of.removable_lots.length} lot(s) à supprimer</span>
                        {of.blocking_lots.length > 0 && (
                          <span className="text-[#C05C47] italic"> · {of.blocking_lots.length} lot(s) bloquant(s)</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {impact.bl_to_cancel && (
                  <div className="border border-[#E5E5E0] rounded p-2.5 bg-[#FAFAF7]">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-[#1A1C1A]">
                      <FileText className="w-3.5 h-3.5 text-[#2D5A87]" />
                      BL <span className="font-mono">{impact.bl_to_cancel.reference}</span> (statut {impact.bl_to_cancel.status}) sera annulé
                    </div>
                  </div>
                )}

                {impact.of_to_cancel_simple.length === 0 && impact.of_to_cancel_with_lots.length === 0 && !impact.bl_to_cancel && (
                  <div className="text-xs text-[#575A57] italic">
                    Aucune entité liée — annulation simple de la commande.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="co-cancel-cascade-cancel">Garder</AlertDialogCancel>
          {impact && !impact.blocked && (
            <AlertDialogAction
              onClick={apply}
              disabled={applying}
              className="bg-[#C05C47] hover:bg-[#A04634]"
              data-testid="co-cancel-cascade-apply"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Annuler définitivement
            </AlertDialogAction>
          )}
          {impact && impact.blocked && (
            <button
              type="button"
              onClick={forceApply}
              disabled={applying}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-[#7A3324] hover:bg-[#5C2418] text-white h-10 px-4 py-2 disabled:opacity-50"
              data-testid="co-cancel-cascade-force"
              title="Ignore les lots déjà utilisés — la traçabilité HACCP est préservée (lots archivés, mouvements conservés)"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Annuler quand même (force)
            </button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
